/**
 * A league: overview, draft, rosters, pool, schedule, standings, and (for its players and staff) trades, teams and
 * running it. It updates by itself when anything changes, here or on Showdown.
 */
import type { PokemonData } from '@ogreclan/pokemon-core';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { api, ApiError, signInUrl, type ActionResult, type LeagueView } from '../api';
import { DraftTab } from '../league/draft';
import { ManageTab } from '../league/manage';
import { OverviewTab } from '../league/overview';
import { PoolTab } from '../league/pool';
import { RostersTab } from '../league/rosters';
import { ScheduleTab } from '../league/schedule';
import { StandingsTab } from '../league/standings';
import { TeamsTab } from '../league/teams';
import { TradesTab } from '../league/trades';
import { useMe } from '../me';
import { usePokemon } from '../pokemon';
import { Link } from '../router';
import { ErrorBox, Loading, toast } from '../ui';
import { DiscordLeaguePage } from './discord-league';
import { phaseClass, phaseText } from './leagues';

export interface LeagueProps {
	view: LeagueView;
	data: PokemonData | null;
	/** Runs a /draft command on Showdown as you, and says how it went (`done` if Showdown says nothing); true if it worked */
	act: (command: string, args?: string, done?: string) => Promise<boolean>;
}

interface Tab {
	id: string;
	label: string;
	show: (view: LeagueView) => boolean;
	component: (props: LeagueProps) => preact.JSX.Element;
}

const TABS: Tab[] = [
	{ id: 'overview', label: 'Overview', show: () => true, component: OverviewTab },
	{ id: 'draft', label: 'Draft', show: () => true, component: DraftTab },
	{ id: 'rosters', label: 'Rosters', show: () => true, component: RostersTab },
	{ id: 'pool', label: 'Pool', show: () => true, component: PoolTab },
	{ id: 'schedule', label: 'Schedule', show: v => v.league.schedule.length > 0, component: ScheduleTab },
	{ id: 'standings', label: 'Standings', show: v => v.league.schedule.length > 0, component: StandingsTab },
	{ id: 'trades', label: 'Trades', show: v => !!v.you && v.league.phase === 'season', component: TradesTab },
	{ id: 'teams', label: 'My teams', show: v => !!v.you && v.league.schedule.length > 0, component: TeamsTab },
	{ id: 'manage', label: 'Run the league', show: v => v.role === 'staff', component: ManageTab },
];

export function LeaguePage({ roomid, tab }: { roomid: string, tab?: string }) {
	const me = useMe();
	const [view, setView] = useState<LeagueView | null>(null);
	/** The Discord league this room's address is (its league here is only that league's copy for the Draft Planner) */
	const [botLeague, setBotLeague] = useState<number | null>(null);
	const [error, setError] = useState<unknown>(null);
	const revision = useRef(0);
	const data = usePokemon(view?.mod);

	const load = useCallback(async () => {
		try {
			const loaded = await api.get<LeagueView | { botLeague: number }>(`/api/leagues/${encodeURIComponent(roomid)}`);
			setBotLeague(loaded.botLeague ?? null);
			if ('league' in loaded) {
				revision.current = loaded.revision;
				setView(loaded);
			}
			setError(null);
		} catch (e) {
			setError(e);
		}
	}, [roomid]);

	useEffect(() => {
		setView(null);
		setBotLeague(null);
		void load();
	}, [roomid, me?.user?.showdown?.id]);

	// changes made anywhere (here, on Showdown, or by the pick timer) arrive as a new revision
	useEffect(() => {
		if (botLeague !== null) return;
		const source = new EventSource(`/api/leagues/${encodeURIComponent(roomid)}/stream`);
		source.addEventListener('revision', event => {
			const next = Number((event as MessageEvent).data);
			if (next === -1) setError(new ApiError(`This league was deleted.`, 404));
			else if (next !== revision.current) void load();
		});
		return () => source.close();
	}, [roomid, botLeague]);

	const act = useCallback(async (command: string, args = '', done = 'Done.') => {
		try {
			const result = await api.post<ActionResult>(`/api/leagues/${encodeURIComponent(roomid)}/actions`, { command, args });
			toast(result.replies.join('\n') || done, 'good');
			void load();
			return true;
		} catch (e) {
			toast(e instanceof Error ? e.message : String(e), 'bad');
			return false;
		}
	}, [roomid]);

	if (error) {
		return <>
			<ErrorBox error={error} />
			{error instanceof ApiError && error.status === 404 && !me?.user && <p>If it's a private league,{' '}
				<a href={signInUrl()}>sign in</a> to see it.</p>}
			<p><Link href="/league">All leagues</Link></p>
		</>;
	}
	if (botLeague !== null) return <DiscordLeaguePage id={String(botLeague)} tab={tab} />;
	if (!view) return <Loading />;

	const tabs = TABS.filter(t => t.show(view));
	const current = tabs.find(t => t.id === tab) || tabs[0]!;
	const Component = current.component;
	const { league } = view;
	return <>
		<div class="league-head">
			<div>
				<p class="small" style="margin:0"><Link href="/league">Leagues</Link> /</p>
				<h1>{view.title}</h1>
				<p class="muted small" style="margin:0">
					<span class={`badge ${phaseClass(league.phase)}`}>{phaseText(league.phase, league.week)}</span>{' '}
					{league.settings.format.split('@@@')[0]!.replace(/^\/challenge\s+/i, '')} ·{' '}
					{league.settings.type === 'points' ? `${league.settings.budget} points, ` : ''}{league.settings.picks} Pokémon each
					{view.isPrivate && <> · <span class="badge">Private</span></>}
				</p>
			</div>
			{me?.user && !me.user.showdown && <p class="notice small" style="margin:0">
				<Link href="/account">Link your Showdown name</Link> to play here.
			</p>}
		</div>
		<nav class="tabs">
			{tabs.map(t => <Link key={t.id} href={`/league/${view.roomid}${t.id === 'overview' ? '' : `/${t.id}`}`}
				class={t === current ? 'active' : ''}>
				{t.label}{t.id === 'draft' && league.phase === 'draft' && <> <span class="badge good">Live</span></>}
			</Link>)}
		</nav>
		<Component view={view} data={data} act={act} />
	</>;
}
